import { apiSlice } from "store/queryApi";
import AssetsAPI, { IPostGetCbDataSupaParams } from "core/api/AssetsApi";
import {
  IGenerateAssetOverallInformation, IGenerateConditionMonitoringCard,
  IPostGetTimeseriesParams, RMS,
  temperature as temperatureType,
} from "types/assetsApi";
import { IInitialStateAsset } from "types/store";
import {
  isValid, millisecondsToSeconds,
  minutesToMilliseconds, subMinutes,
} from "date-fns";
import { RootState } from "store/store";
import { setGlobalTimespan } from "store/rootSlice";
import { shouldPollData } from "core/helpers";
import { supabase } from "core/api/supabase";

import { createSlice } from "@reduxjs/toolkit";

const selectInferenceMeasurement = (api: any, arg: any) => {
  const state = api.getState() as any as RootState
  const selectMeasurement =
    arg.select === "temperature"
      ? state.global.temperatureGraphSelectedRange
      : state.global.vibrationGraphSelectedRange

  const fromEpoch = isValid(selectMeasurement.fromEpoch)
    ? millisecondsToSeconds(
        subMinutes(new Date(), selectMeasurement.fromEpoch).valueOf(),
      )
    : null
  const toEpoch = isValid(selectMeasurement.fromEpoch)
    ? millisecondsToSeconds(new Date().valueOf())
    : null

  return { ...arg, fromEpoch, toEpoch }
}

const initialState: IInitialStateAsset = {
  assetSelected: { asset_id: "", assetId: "", assetName: " ", machineId: "" },
  assetAxisSelected: "x",
  assetTypeVelocity: "acceleration",
  lastArgs: {},
}

export const assetsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    generateAssetOverallInfo: builder.query<
      any,
      IGenerateAssetOverallInformation
    >({
      query: (asset: IGenerateAssetOverallInformation) => ({
        api: AssetsAPI.generateAssetOveralInfrormation(asset),
      }),
    }),
    inferenceGraphData: builder.query<any, any>({
      queryFn: (arg, api) => {
        const newArgs = selectInferenceMeasurement(api, arg)
        return AssetsAPI.getBeatBoxRMSByBeatBoxIDFormedTimeFrame(newArgs)
      },
    }),
    conditionMonitoring: builder.query<any, IGenerateConditionMonitoringCard>({
      query: (cm: IGenerateConditionMonitoringCard) => ({
        api: AssetsAPI.generateConditionMonitoringCard(cm),
      }),
      transformResponse: (response: any) => {
        const {
          acceleration_x: acc_x,
          acceleration_y: acc_y,
          acceleration_z: acc_z,
          temperature,
        } = response

        return { acc_x, acc_y, acc_z, temperature }
      },
    }),
    assetInfo: builder.query<any, any>({
      query: (assetId: string) => ({
        api: supabase
          .from("asset")
          .select("*")
          .eq("asset_id", assetId)
          .limit(1)
          .single(),
      }),
    }),

    getRMS: builder.query<any, any>({
      queryFn: (
        { assetId, axis, fromEpoch, shouldPoll, toEpoch, pollingInterval }: RMS,
        { getState },
      ) => {
        const state = getState() as any as RootState
        const { globalTimespan } = state.global
        const rmsParamsObj = {
          assetId,
          axis,
          fromEpoch,
          shouldPoll,
          toEpoch,
          pollingInterval,
        }
        if (shouldPoll) {
          const { fulfilledTimeStamp } =
            assetsApiSlice.endpoints.getRMS.select(rmsParamsObj)(state)
          if (fulfilledTimeStamp) {
            shouldPollData(
              rmsParamsObj,
              setGlobalTimespan(globalTimespan),
              fulfilledTimeStamp,
              pollingInterval ?? minutesToMilliseconds(15),
            )
          }
        }
        return AssetsAPI.getRMS(rmsParamsObj)
      },
    }),
    getTemp: builder.query<any, any>({
      query: ({ assetId, start_time }: temperatureType) => ({
        api: AssetsAPI.getTemp({
          assetId,
          start_time,
          end_time: millisecondsToSeconds(new Date().valueOf()),
        }),
      }),
    }),

    getLastTemp: builder.query<any, any>({
      query: ({ assetId }: temperatureType) => ({
        api: supabase
          .from("metrics")
          .select("temperature")
          .eq("asset_id", assetId)
          .order("time", { ascending: false })
          .limit(1)
          .single(),
      }),
    }),
    postGetTsData: builder.query<any, any>({
      query: (body: IPostGetTimeseriesParams) => ({
        api: AssetsAPI.postGetTsData({
          ...body,
          end_time: new Date().toISOString(),
        }),
      }),

      transformResponse: (response: any) => {
        const emptyResponse = { ...response, data: [] }

        // Edge case: Handle null/undefined or non-object responses
        if (typeof response !== "object" || response === null) {
          return emptyResponse
        }

        const { data } = response

        // Edge case: Handle null/undefined or non-array data
        if (!Array.isArray(data)) {
          return emptyResponse
        }

        // Edge case: Handle empty data array
        if (data.length === 0) {
          return response // Return the input response as-is
        }

        // Refactored transformation to handle invalid data formats
        const transformedResponse = data.map((item) => {
          if (!Array.isArray(item) || item.length !== 2) {
            return emptyResponse
          }

          const [date, value] = item

          // Edge case: Handle invalid date or value types
          if (typeof date !== "string" || typeof value !== "number") {
            return emptyResponse
          }

          return { date, value }
        })

        return { ...response, data: transformedResponse }
      },
    }),
    getVelocity: builder.query<any, any>({
      queryFn: (
        { assetId, axis, fromEpoch, shouldPoll, toEpoch, pollingInterval }: RMS,
        { getState },
      ) => {
        const state = getState() as any as RootState
        const { globalTimespan } = state.global
        const velocityParamsObj = {
          assetId,
          axis,
          fromEpoch,
          shouldPoll,
          toEpoch,
          pollingInterval,
        }

        if (shouldPoll) {
          const { fulfilledTimeStamp } =
            assetsApiSlice.endpoints.getVelocity.select(velocityParamsObj)(
              state,
            )
          if (fulfilledTimeStamp) {
            shouldPollData(
              velocityParamsObj,
              setGlobalTimespan(globalTimespan),
              fulfilledTimeStamp,
              pollingInterval ?? 15,
            )
          }
        }
        return AssetsAPI.getVelocity(velocityParamsObj)
      },
    }),
    postGetCbDataSupa: builder.query<any, any>({
      queryFn: async (params: IPostGetCbDataSupaParams) => {
        const data = await AssetsAPI.postGetCbDataSupa(params)

        return {
          data: data,
        }
      },
    }),

    postGetHealthStateGraph: builder.query<any, any>({
      queryFn: async (params: {
        start_time: string
        end_time: string
        machine_id: string
      }) => {
        const data = await supabase
          .from("machine_condition")
          .select("state_ui,time")
          .eq("machine_id", params.machine_id)

        const tempData = data.data

        const response = tempData?.map((item: any) => ({
          x: new Date(item.time).getTime(),
          y: item.state_ui,
        }))

        return {
          data: response,
        }
      },
    }),
  }),
})

const assetsSlice = createSlice({
  name: "assets",
  initialState,
  reducers: {
    setAssetAxisSelected: (state, action) => {
      state.assetAxisSelected = action.payload
    },
    setAssetTypeVelocity: (state, action) => {
      state.assetTypeVelocity = action.payload
    },

    setSelectedAsset: (state, action) => {
      state.assetSelected = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      assetsApiSlice.endpoints.inferenceGraphData.matchFulfilled,
      (state, action) => {
        state.lastArgs = action.meta.arg.originalArgs
      },
    )
  },
})

export const {
  useConditionMonitoringQuery,
  useLazyInferenceGraphDataQuery,
  useInferenceGraphDataQuery,
  useAssetInfoQuery,
  useGetRMSQuery,
  useGetTempQuery,
  usePostGetTsDataQuery,
  useGetLastTempQuery,
  usePostGetCbDataSupaQuery,
  useGetVelocityQuery,
  usePostGetHealthStateGraphQuery,
} = assetsApiSlice

export const { setAssetAxisSelected, setSelectedAsset, setAssetTypeVelocity } =
  assetsSlice.actions

export default assetsSlice.reducer
